import { memo } from 'react';

type Key = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '0' | '.' | 'back';

const KEYS: Key[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'];

/** Apply a numpad key to a decimal-amount string. Shared by every amount entry
 *  (send, card top-up / withdraw / deposit) so the input behaves identically.
 *  An empty string is treated as "0" — deleting the last digit returns "0".
 *  `maxDecimals` caps the fraction digits (BTC 8 / ETH 6 / stables 2);
 *  undefined = unlimited (backward-compatible). */
export function applyAmountKey(prev: string, k: Key, maxDecimals?: number): string {
  if (k === 'back') return prev.length <= 1 ? '0' : prev.slice(0, -1);
  if (k === '.') {
    if (maxDecimals === 0) return prev;
    return prev.includes('.') ? prev : `${prev === '' ? '0' : prev}.`;
  }
  if (maxDecimals != null) {
    const frac = prev.split('.')[1];
    if (frac !== undefined && frac.length >= maxDecimals) return prev;
  }
  if (prev === '' || prev === '0') return k;
  return prev + k;
}

function Backspace() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <path
        d="M20 12H5M11 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const Numpad = memo(function Numpad({
  onKey,
  className,
  decimal = true,
}: {
  onKey: (k: Key) => void;
  className?: string;
  /** Show the decimal "." key. When false, that cell is left empty/inactive. */
  decimal?: boolean;
}) {
  return (
    <div className={`grid grid-cols-3 gap-x-2 gap-y-1.5 ${className ?? ''}`}>
      {KEYS.map((k) =>
        k === '.' && !decimal ? (
          <div key={k} aria-hidden />
        ) : (
          <button
            key={k}
            type="button"
            // Respond on pointer-down (not click) so the digit registers instantly
            // — avoids the touch→click delay that makes fast typing feel laggy.
            onPointerDown={() => onKey(k)}
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            className="flex h-16 select-none items-center justify-center rounded-[28px] bg-white text-[26px] font-medium text-ink shadow-[0px_1px_2px_rgba(16,24,40,0.04)] transition-colors active:bg-black/[0.04]"
          >
            {k === 'back' ? <Backspace /> : k}
          </button>
        ),
      )}
    </div>
  );
});

export type { Key as NumpadKey };
